import { supabase } from '../lib/supabase';
import { errorHandler } from '../utils/errorHandler';
import { validationUtils } from '../utils/validation';

export const authService = {
  async signUpStudent(userData) {
    return errorHandler?.handleAsync(async () => {
      const { email, password, fullName, mobile, city, state, courseInterest, examStatus, budgetRange, studyMode, consentGiven, utmSource, utmCampaign } = userData;
      
      // Validate inputs
      if (!validationUtils?.isValidEmail(email)) {
        throw new Error('Invalid email format');
      }
      if (!validationUtils?.isStrongPassword(password)) {
        throw new Error('Password must be at least 8 characters with uppercase, lowercase, and number');
      }
      if (!validationUtils?.isValidPhone(mobile)) {
        throw new Error('Invalid mobile number');
      }
      
      // Sanitize inputs
      const sanitizedData = {
        email: validationUtils?.sanitizeString(email)?.toLowerCase(),
        fullName: validationUtils?.sanitizeString(fullName),
        mobile: validationUtils?.sanitizeString(mobile)?.replace(/[\s()-]/g, ''),
        city: validationUtils?.sanitizeString(city),
        state: validationUtils?.sanitizeString(state),
        courseInterest: validationUtils?.sanitizeString(courseInterest),
        examStatus: validationUtils?.sanitizeString(examStatus),
        budgetRange: validationUtils?.sanitizeString(budgetRange),
        studyMode: validationUtils?.sanitizeString(studyMode)
      };
      
      // Get the base URL for redirect
      const baseUrl = window.location?.origin;
      const redirectUrl = `${baseUrl}/auth/callback`;
      
      // Create auth user
      const { data: authData, error: authError } = await supabase?.auth?.signUp({
        email: sanitizedData?.email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            role: 'student',
            name: sanitizedData?.fullName
          }
        }
      });
      
      if (authError) throw authError;
      
      // Insert into users table
      const { data: user, error: userError } = await supabase?.from('users')?.insert({
          id: authData?.user?.id,
          email: sanitizedData?.email,
          phone: sanitizedData?.mobile,
          role: 'student',
          name: sanitizedData?.fullName,
          city: sanitizedData?.city,
          state: sanitizedData?.state,
          is_verified: false,
          password_hash: 'supabase_auth_managed'
        })?.select()?.single();
      
      if (userError) throw userError;
      
      // Insert into student_profiles table with auto-approved status
      const { error: profileError } = await supabase?.from('student_profiles')?.insert({
          user_id: authData?.user?.id,
          course_interest: sanitizedData?.courseInterest,
          exam_status: sanitizedData?.examStatus,
          budget_range: sanitizedData?.budgetRange,
          study_mode: sanitizedData?.studyMode,
          consent_given: consentGiven,
          utm_source: utmSource,
          utm_campaign: utmCampaign,
          approval_status: 'approved',
          approved_at: new Date()?.toISOString(),
          is_restricted: false
        });
      
      if (profileError) throw profileError;

      // Auto sign-in after successful registration so the user has an active session.
      // The app uses custom OTP verification, so we sign in immediately without
      // waiting for Supabase's email confirmation flow.
      const { data: signInData, error: signInError } = await supabase?.auth?.signInWithPassword({
        email: sanitizedData?.email,
        password
      });

      if (signInError) {
        // Sign-in failed (e.g. email confirmation required in Supabase settings).
        // Return the created user anyway — the registration succeeded.
        console.warn('Auto sign-in after registration failed:', signInError?.message);
      }
      
      return { user: signInData?.user || authData?.user, profile: user };
    }, 'signUpStudent');
  },

  async signInWithEmail(email, password) {
    return errorHandler?.handleAsync(async () => {
      // Validate inputs
      if (!validationUtils?.isValidEmail(email)) {
        throw new Error('Invalid email format');
      }
      if (!password || password?.length < 8) {
        throw new Error('Invalid password');
      }
      
      const sanitizedEmail = validationUtils?.sanitizeString(email)?.toLowerCase();
      
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email: sanitizedEmail,
        password
      });
      
      if (error) throw error;
      
      // Fetch user profile with role
      const { data: userProfile, error: profileError } = await supabase?.from('users')?.select('*')?.eq('id', data?.user?.id)?.single();
      
      if (profileError) throw profileError;
      
      return { user: data?.user, session: data?.session, profile: userProfile };
    }, 'signInWithEmail');
  },

  async signOut() {
    return errorHandler?.handleAsync(async () => {
      const { error } = await supabase?.auth?.signOut();
      if (error) throw error;
    }, 'signOut');
  },

  async getCurrentUser() {
    return errorHandler?.handleAsync(async () => {
      const { data: { user }, error } = await supabase?.auth?.getUser();
      if (error) throw error;
      
      if (!user) return null;
      
      // Fetch user profile
      const { data: profile, error: profileError } = await supabase?.from('users')?.select('*')?.eq('id', user?.id)?.single();
      
      if (profileError) throw profileError;
      
      return { user, profile };
    }, 'getCurrentUser');
  },

  async getUserProfile(userId) {
    return errorHandler?.handleAsync(async () => {
      if (!validationUtils?.isValidUUID(userId)) {
        throw new Error('Invalid user ID');
      }
      
      // Explicitly query from public.users to avoid ambiguity with auth.users
      const { data, error } = await supabase
        ?.from('users')
        ?.select(`
          *,
          student_profiles!student_profiles_user_id_fkey(*),
          institute_profiles!institute_profiles_user_id_fkey(*)
        `)
        ?.eq('id', userId)
        ?.single();
      
      if (error) throw error;
      return data;
    }, 'getUserProfile');
  }
};